import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customerAPI, type CreateCustomerProjectionData, type CreateSubscriptionTierData, type CreateCustomerCapacityGroupData } from "../services/api/customer.service";
import { financialKeys } from "./useFinancialData";

export const useCustomerProjections = (startupId: string) => {
  return useQuery({
    queryKey: ["customer-projections", startupId],
    queryFn: () => customerAPI.getCustomerProjections(startupId),
    enabled: !!startupId,
  });
};

export const useSubscriptionTiers = (startupId: string) => {
  return useQuery({
    queryKey: ["subscription-tiers", startupId],
    queryFn: () => customerAPI.getSubscriptionTiers(startupId),
    enabled: !!startupId,
  });
};

export const useCustomerGrowthScenarios = (startupId: string) => {
  return useQuery({
    queryKey: ["customer-growth-scenarios", startupId],
    queryFn: () => customerAPI.getCustomerGrowthScenarios(startupId),
    enabled: !!startupId,
  });
};

export const useAcquisitionCostModels = (startupId: string) => {
  return useQuery({
    queryKey: ["acquisition-cost-models", startupId],
    queryFn: () => customerAPI.getAcquisitionCostModels(startupId),
    enabled: !!startupId,
  });
};

export const useCapacityGroups = (startupId: string) => {
  return useQuery({
    queryKey: ["capacity-groups", startupId],
    queryFn: () => customerAPI.getCapacityGroups(startupId),
    enabled: !!startupId,
  });
};

export const useCreateCapacityGroup = (startupId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomerCapacityGroupData) =>
      customerAPI.createCapacityGroup(startupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capacity-groups", startupId] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
};

export const useUpdateCapacityGroup = (startupId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      data,
    }: {
      groupId: string;
      data: Partial<CreateCustomerCapacityGroupData>;
    }) => customerAPI.updateCapacityGroup(startupId, groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capacity-groups", startupId] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
};

export const useDeleteCapacityGroup = (startupId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => customerAPI.deleteCapacityGroup(startupId, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capacity-groups", startupId] });
      queryClient.invalidateQueries({ queryKey: ["customer-projections", startupId] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
};

export const useCreateCustomerProjection = (startupId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomerProjectionData) => customerAPI.createCustomerProjection(startupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-projections", startupId] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
};

export const useUpdateCustomerProjection = (startupId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectionId, data }: { projectionId: string; data: Partial<CreateCustomerProjectionData> }) =>
      customerAPI.updateCustomerProjection(startupId, projectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-projections", startupId] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
};

export const useDeleteCustomerProjection = (startupId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectionId: string) => customerAPI.deleteCustomerProjection(startupId, projectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-projections", startupId] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
};

export const useCreateSubscriptionTier = (startupId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSubscriptionTierData) => customerAPI.createSubscriptionTier(startupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-tiers", startupId] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
};

export const useUpdateSubscriptionTier = (startupId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tierId, data }: { tierId: string; data: Partial<CreateSubscriptionTierData> }) =>
      customerAPI.updateSubscriptionTier(startupId, tierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-tiers", startupId] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
};

export const useDeleteSubscriptionTier = (startupId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tierId: string) => customerAPI.deleteSubscriptionTier(startupId, tierId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-tiers", startupId] });
      queryClient.invalidateQueries({ queryKey: financialKeys.complete(startupId) });
    },
  });
};